import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Card from 'components/UI/Card';
import Heading from 'components/UI/Heading';
import PageWrapper from 'components/UI/PageWrapper';
import * as routes from 'router/Routes';
import InputField from 'components/UI/InputField';
import { Grid } from 'components/software/SoftwareTable/SoftwareFilter/styled';
import Button from 'components/UI/Button';
import { ButtonGrid } from 'styles';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { SoftwareAttribute } from 'models/husqiotbus';
import { Form } from 'components/UI/SearchField/styled';
import {
  storeSoftware,
  softwareUploadSelector,
} from 'store/softwareState/softwareState';
import Loader from 'components/UI/Loader';
import {
  releaseStageOptions,
  SoftwareCategoryAttribute,
  SoftwareFilterAttribute,
  SoftwareMimeTypeAttribute,
  SoftwarePurpose,
  SoftwarePurposeAttribute,
  SoftwareReleaseStageAttribute,
  SoftwareUploadSuccess,
} from 'models';
import { showErrorToast } from 'utils/toast';
import CardHeader from 'components/UI/Card/CardHeader';
import SelectField, { OptionType } from 'components/UI/SelectField';
import { useNavigate } from 'react-router';

const UploadSoftwarePage: FC = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const [purposes, setPurposes] = useState<OptionType[]>([]);
  const { isLoading, error, data }: SoftwareUploadSuccess = useAppSelector(
    softwareUploadSelector
  );
  // Watch
  const [watchPurpose] = watch([
    SoftwarePurposeAttribute.PURPOSE,
    SoftwarePurposeAttribute.AFTERSALE_SERVICE,
    SoftwarePurposeAttribute.FOTA,
    SoftwarePurposeAttribute.ASSEMBLY,
    SoftwarePurposeAttribute.MANUFACTURING,
  ]);
  const [watchCategory] = watch([
    SoftwareCategoryAttribute.CATEGORY,
    SoftwareCategoryAttribute.ACCESSORIES,
    SoftwareCategoryAttribute.BATTERY_TESTER,
    SoftwareCategoryAttribute.CONSTRUCTION,
    SoftwareCategoryAttribute.HANDHELD,
    SoftwareCategoryAttribute.ROBOTICS,
    SoftwareCategoryAttribute.WATERING,
    SoftwareCategoryAttribute.WHEELED,
  ]);
  const [watchReleaseStage] = watch([
    SoftwareReleaseStageAttribute.RELEASE_STAGE,
    SoftwareReleaseStageAttribute.RELEASED,
    SoftwareReleaseStageAttribute.EXTERNAL_TEST,
    SoftwareReleaseStageAttribute.INTERNAL_TEST,
    SoftwareReleaseStageAttribute.INVALIDATED,
  ]);
  const [watchMimeType] = watch([
    SoftwareMimeTypeAttribute.MIME_TYPE,
    SoftwareMimeTypeAttribute.APPLICATION_BIN,
    SoftwareMimeTypeAttribute.HEX,
    SoftwareMimeTypeAttribute.MTO,
    SoftwareMimeTypeAttribute.SWU,
    SoftwareMimeTypeAttribute.ZIP,
  ]);

  // Status
  const statusOptions: OptionType[] = [
    {
      key: 'AFTERSALE_SERVICE',
      label: 'AFTERSALE_SERVICE',
      value: SoftwarePurpose.AFTERSALE_SERVICE,
    },
    {
      key: 'ASSEMBLY',
      label: 'ASSEMBLY',
      value: SoftwarePurpose.ASSEMBLY,
    },
    {
      key: 'FOTA',
      label: 'FOTA',
      value: SoftwarePurpose.FOTA,
    },
    {
      key: 'MANUFACTURING',
      label: 'MANUFACTURING',
      value: SoftwarePurpose.MANUFACTURING,
    },
  ];

  const categoryStatusOptions: OptionType[] = [
    {
      key: 'ROBOTICS',
      label: 'ROBOTICS',
      value: SoftwareCategoryAttribute.ROBOTICS,
    },
    {
      key: 'WHEELED',
      label: 'WHEELED',
      value: SoftwareCategoryAttribute.WHEELED,
    },
    {
      key: 'CONSTRUCTION',
      label: 'CONSTRUCTION',
      value: SoftwareCategoryAttribute.CONSTRUCTION,
    },
    {
      key: 'HANDHELD',
      label: 'HANDHELD',
      value: SoftwareCategoryAttribute.HANDHELD,
    },
    {
      key: 'ACCESSORIES',
      label: 'ACCESSORIES',
      value: SoftwareCategoryAttribute.ACCESSORIES,
    },
    {
      key: 'WATERING',
      label: 'WATERING',
      value: SoftwareCategoryAttribute.WATERING,
    },
    {
      key: 'BATTERY_TESTER',
      label: 'BATTERY_TESTER',
      value: SoftwareCategoryAttribute.BATTERY_TESTER,
    },
  ];

  const mimeTypeOptions: OptionType[] = [
    {
      key: 'APPLICATION_BIN',
      label: 'application/bin',
      value: SoftwareMimeTypeAttribute.APPLICATION_BIN,
    },
    {
      key: 'HEX',
      label: 'hex',
      value: SoftwareMimeTypeAttribute.HEX,
    },
    {
      key: 'MTO',
      label: 'mto',
      value: SoftwareMimeTypeAttribute.MTO,
    },
    {
      key: 'SWU',
      label: 'swu',
      value: SoftwareMimeTypeAttribute.SWU,
    },
    {
      key: 'ZIP',
      label: 'zip',
      value: SoftwareMimeTypeAttribute.ZIP,
    },
  ];
  // Navigation
  const navigate = useNavigate();

  // Select option
  const onSelectPurpose = useCallback(
    (name: SoftwarePurposeAttribute) => (value: OptionType[]) => {
      setPurposes(value);
      setValue(name, value.toString());
    },
    [setPurposes, setValue]
  );
  // Select option
  const onSelectField = useCallback(
    (name: string) => (value: string) => {
      setValue(name, value);
    },
    [setValue]
  );

  const loadingSpinner = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
  }, [isLoading]);

  // Show error
  useEffect(() => {
    if (error) {
      showErrorToast(
        `${intl.formatMessage({
          id: 'software_upload.error',
        })}. Error: ${error.title}`
      );
    }
    if (data) {
      navigate('/softwares', { replace: true });
    }
  }, [dispatch, intl, error, data, navigate]);
  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      dispatch(storeSoftware(values, purposes));
    },
    [dispatch, purposes]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <PageWrapper>
        <Heading>
          <FormattedMessage id="software_upload.title" />
        </Heading>
        <Card>
          <CardHeader navUrl={routes.SOFTWARES} title="software.btn_back" />
          <Grid>
            <InputField
              description="software.name"
              error={errors.name}
              register={register(SoftwareAttribute.Name, {
                required: {
                  value: true,
                  message: 'input.required',
                },
              })}
            />
            <InputField
              description="software.version"
              error={errors.version}
              register={register(SoftwareAttribute.Version, {
                required: { value: true, message: 'input.required' },
              })}
            />
            <InputField
              description="software_upload.pnc"
              error={errors.pnc}
              register={register(SoftwareAttribute.Pnc, {
                required: { value: true, message: 'input.required' },
              })}
            />
          </Grid>
          <Grid>
            <InputField
              description="software_upload.revision"
              error={errors.revision}
              register={register(SoftwareAttribute.Revision, {
                required: { value: true, message: 'input.required' },
              })}
            />
            <SelectField
              name={SoftwareCategoryAttribute.CATEGORY}
              error={errors.category}
              register={register(SoftwareCategoryAttribute.CATEGORY, {
                required: { value: true, message: 'required' },
              })}
              onSelect={onSelectField(SoftwareCategoryAttribute.CATEGORY)}
              placeholder={intl.formatMessage({ id: 'select.placeholder' })}
              label={intl.formatMessage({ id: 'software.category' })}
              defaultValue={watchCategory}
              options={categoryStatusOptions}
            />
            <SelectField
              name={SoftwareFilterAttribute.PURPOSE}
              error={errors.purpose}
              register={register(SoftwarePurposeAttribute.PURPOSE, {
                required: { value: true, message: 'required' },
              })}
              onMultiSelect={onSelectPurpose(SoftwarePurposeAttribute.PURPOSE)}
              placeholder={intl.formatMessage({ id: 'select.placeholder' })}
              label={intl.formatMessage({ id: 'software.purposes' })}
              defaultValue={watchPurpose}
              options={statusOptions}
              multiple={true}
            />
          </Grid>
          <Grid>
            <InputField
              description="software_upload.firmware_version"
              error={errors.firmwareVersion}
              register={register(SoftwareAttribute.FirmwareVersion, {
                required: { value: true, message: 'input.required' },
              })}
            />
            <InputField
              description="software_upload.bundle_id"
              error={errors.bundelId}
              register={register(SoftwareAttribute.BundelId, {
                required: { value: true, message: 'input.required' },
              })}
            />
            <SelectField
              name={SoftwareMimeTypeAttribute.MIME_TYPE}
              error={errors.mimeType}
              register={register(SoftwareMimeTypeAttribute.MIME_TYPE, {
                required: { value: true, message: 'required' },
              })}
              onSelect={onSelectField(SoftwareMimeTypeAttribute.MIME_TYPE)}
              placeholder={intl.formatMessage({ id: 'select.placeholder' })}
              label={intl.formatMessage({ id: 'software_upload.mimetype' })}
              defaultValue={watchMimeType}
              options={mimeTypeOptions}
            />
          </Grid>
          <Grid>
            <SelectField
              name={SoftwareReleaseStageAttribute.RELEASE_STAGE}
              error={errors.releaseStage}
              register={register(SoftwareReleaseStageAttribute.RELEASE_STAGE, {
                required: { value: true, message: 'required' },
              })}
              onSelect={onSelectField(
                SoftwareReleaseStageAttribute.RELEASE_STAGE
              )}
              placeholder={intl.formatMessage({ id: 'select.placeholder' })}
              label={intl.formatMessage({ id: 'software.releaseStage' })}
              defaultValue={watchReleaseStage}
              options={releaseStageOptions}
            />
            <InputField
              description="software_upload.file"
              type="file"
              error={errors.file}
              register={register('files')}
            />
          </Grid>
          {loadingSpinner}
          <ButtonGrid>
            <Button submit>
              <FormattedMessage id="software_upload.btn_save" />
            </Button>
          </ButtonGrid>
        </Card>
      </PageWrapper>
    </Form>
  );
};

export default UploadSoftwarePage;
